'use client';

import React, { useState } from 'react';
import { Form, Formik, FormikValues } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { sendEmailAction } from '@/app/actions/resendEmail';
import { FormData, FormField, Site } from '@/components/data/dataType';
import { getFieldValidation } from '@/app/helper/validateForm';
import { FormikField } from '@/components/data/FormikField';
import Modal from '@/components/ui/Modal';
import { createClient } from '@/app/utils/supabase/client';
import { DataFieldType } from '@/app/[locale]/admin/lib/DataFieldType';
import { formFields } from './forms/formFields';
import {
    generateNameTitleMap,
    getNewPosition,
    setFieldValues,
    sortAscBy,
} from '@/app/[locale]/admin/lib/util';
import { FaPen, FaTrash } from 'react-icons/fa6';
import dynamic from 'next/dynamic';
import ToastMessage from '@/components/ui/ToastMessage';
import { getFilteredFields } from './forms/utils';
import EditForm from './forms/EditForm';
import { AttributesInput } from '../../../components/ui/AttributesInput';
import { HiOutlineCog } from 'react-icons/hi2';
import { MdFormatListBulletedAdd } from 'react-icons/md';
import { saveFormResponse } from '@/app/actions/saveFormResponse';
import { revalidateCacheByTag } from '@/app/api/actions';

const SendingForm: React.FC<{
    editable: boolean;
    formData: FormData;
    locale: string;
    site: Site;
    translation: Record<string, string>;
}> = ({ editable, formData, locale, site, translation }) => {
    const [fields, setFields] = useState<FormField[] | undefined>(
        sortAscBy(formData.formsFields, 'position'),
    );
    const supabase = createClient();
    const [editingField, setEditingField] = useState<FormField | null>(null);
    const [isEditingForm, setIsEditingForm] = useState(false);
    const [showDevModal, setShowDevModal] = useState(false);
    const [devModeValues, setDevModeValues] = useState(null);

    const DeleteConfirm = dynamic(
        () => import('@/components/data/DeleteConfirm'),
        {
            ssr: false,
        },
    );

    const notify = React.useCallback((type: string, message: string) => {
        ToastMessage({ type, message });
    }, []);

    const initialFieldValues: FormField = {
        id: 0,
        name: 'name',
        title: { [locale]: 'title' },
        dataType: DataFieldType.String,
        required: true,
        requiredMessage: { [locale]: 'Le champ est requis' },
        invalidMessage: { [locale]: 'Le champ est invalide' },
        defaultValue: '',
        enabled: true,
        position: getNewPosition(fields, 'position'),
    };

    const formSchema = z.object(
        fields.reduce(
            (schema, field) => {
                schema[field.name] = getFieldValidation(field, locale);
                return schema;
            },
            {} as { [key: string]: z.ZodTypeAny },
        ),
    );

    const handleSubmit = async (
        values: FormikValues,
        { setSubmitting, resetForm },
    ) => {
        if (editable) {
            const responseData = formData.formsFields
                .map(field => {
                    const fieldTitle = field.title[locale] || field.name;
                    const fieldValue =
                        values[field.name] !== undefined &&
                        values[field.name] !== ''
                            ? values[field.name]
                            : 'N/A';
                    return `<p><strong>${fieldTitle}</strong>: ${fieldValue}</p>`;
                })
                .join('');
            setDevModeValues(responseData);
            setShowDevModal(true);
            setSubmitting(false);
            return;
        }

        const schema = generateNameTitleMap(fields, locale);
        const savedResponse = await saveFormResponse(
            formData,
            locale,
            values,
            schema,
        );

        if (savedResponse.success) {
            const footerText = `<p>${translation.footerText1} <strong>"${formData.title[locale]}"</strong></p>
            <p>${translation.footerText2}: <a href="https://${site.domain}/admin/forms/inbox/${formData.id}">ici</a></p>`;
            const response = await sendEmailAction(
                footerText,
                formData,
                locale,
                site,
                values,
            );

            if (response.success) {
                notify('success', translation.success);
                resetForm();
            } else {
                notify('error', translation.error);
            }
        }

        setSubmitting(false);
    };

    const handleAddOrEditField = async (values: FormField) => {
        if (editingField.id !== 0) {
            const { error } = await supabase
                .from('formsFields')
                .update(values)
                .eq('id', editingField.id);
            if (error) {
                notify('error', `Error updating field: ${error.message}`);
            } else {
                notify('success', translation.successUpdate);
                setFields(
                    fields.map(field =>
                        field.id === editingField.id
                            ? { ...field, ...values }
                            : field,
                    ),
                );
                revalidateCacheByTag(`forms-${formData.id}`);
            }
        } else {
            const valuesWithoutId = { ...values };
            delete valuesWithoutId.id;
            const { data, error } = await supabase
                .from('formsFields')
                .insert({ ...valuesWithoutId, formsId: formData.id })
                .select();
            if (error) {
                notify('error', `Error adding field: ${error.message}`);
            } else {
                notify('success', translation.successAdd);
                setFields(sortAscBy([...fields, data[0]], 'position'));
                revalidateCacheByTag(`forms-${formData.id}`);
            }
        }

        setEditingField(null);
    };

    const handleDeleteField = async (fieldId: number) => {
        const { error } = await supabase
            .from('formsFields')
            .delete()
            .eq('id', fieldId);
        if (error) {
            notify('error', 'Error deleting field:' + error.message);
        } else {
            notify('success', translation.successDelete);
            setFields(
                sortAscBy(
                    fields.filter(field => field.id !== fieldId),
                    'position',
                ),
            );
            revalidateCacheByTag(`forms-${formData.id}`);
        }
    };

    const submitChanges = (value: FormikValues, values: FormField) => {
        if (values) {
            const attributes = setFieldValues(
                value,
                values,
                formFields,
                locale,
            );
            const newValue = { ...values, ...attributes, formsId: formData.id };
            handleAddOrEditField(newValue);
        }
    };

    const filteredFields = getFilteredFields(editingField);

    const alignHorizontal = true;

    return (
        <div className="py-2">
            {editable && showDevModal && (
                <Modal
                    closeEvent={() => setShowDevModal(false)}
                    showModal={showDevModal}
                >
                    <div className="px-3 py-1">
                        <h2 className="text-xl font-bold py-2">
                            {translation.modalResponseTitle}
                        </h2>
                        <div
                            className="bg-gray-100 p-2 rounded space-y-2"
                            dangerouslySetInnerHTML={{ __html: devModeValues }}
                        />
                        <div className="flex justify-end">
                            <button
                                className="mt-4 bg-primary text-white py-2 px-4 rounded"
                                onClick={() => setShowDevModal(false)}
                            >
                                {translation.close}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {editable && (
                <>
                    <div className="w-full flex justify-between mb-4">
                        <div className="flex space-x-2 items-center">
                            <button
                                className="text-admin border border-admin hover:bg-admin hover:text-white focus:ring-4 focus:outline-none focus:ring-admin_variant font-medium rounded-md p-1.5 text-center inline-flex items-center"
                                onClick={() => {
                                    setEditingField({
                                        ...initialFieldValues,
                                        position: getNewPosition(
                                            fields,
                                            'position',
                                        ),
                                    });
                                }}
                                type="button"
                            >
                                <MdFormatListBulletedAdd className="h-6 w-6" />
                            </button>
                            <div className="flex items-center">
                                {translation.addField}
                            </div>
                        </div>
                        <>
                            <div className="flex space-x-2 items-center">
                                <div className="flex items-center">
                                    {translation.editForm}
                                </div>
                                <button
                                    className="text-admin border border-admin hover:bg-admin hover:text-white focus:ring-4 focus:outline-none focus:ring-admin_variant font-medium rounded-md p-1.5 text-center inline-flex items-center"
                                    onClick={() => setIsEditingForm(true)}
                                    type="button"
                                >
                                    <HiOutlineCog className="h-6 w-6" />
                                </button>
                            </div>
                            {isEditingForm && (
                                <Modal
                                    closeEvent={() => setIsEditingForm(false)}
                                    showModal={isEditingForm}
                                >
                                    <EditForm
                                        formData={formData}
                                        locale={locale}
                                        onClose={() => setIsEditingForm(false)}
                                        onSuccess={updatedForm => {
                                            // Retourner la notification success ou error de la fonction server
                                            // eslint-disable-next-line no-console
                                            console.log(
                                                'updatedForm',
                                                updatedForm,
                                            );
                                            setIsEditingForm(false);
                                        }}
                                    />
                                </Modal>
                            )}
                        </>
                    </div>
                    <hr className="w-full mx-auto mt-3 mb-6 border border-primary-accent" />
                </>
            )}

            {editingField && (
                <Modal
                    closeEvent={() => {
                        setEditingField(null);
                    }}
                    showModal={!!editingField}
                >
                    <AttributesInput
                        attributes={editingField}
                        fields={filteredFields}
                        locale={locale}
                        onCancel={() => {
                            setEditingField(null);
                        }}
                        onSubmitChanges={(value: FormikValues) =>
                            submitChanges(value, editingField)
                        }
                        title="Form field info"
                    />
                </Modal>
            )}

            <Formik
                initialValues={fields.reduce((acc, field) => {
                    acc[field.name] = field.defaultValue ?? '';
                    return acc;
                }, {})}
                onSubmit={handleSubmit}
                validationSchema={toFormikValidationSchema(formSchema)}
            >
                {({ isSubmitting, touched, dirty, errors }) => (
                    <Form className="space-y-4 w-full">
                        {fields.map(field => (
                            <div
                                className={`flex ${alignHorizontal ? 'flex-row items-center space-x-4' : 'flex-col space-y-2'} w-full`}
                                key={field.id}
                            >
                                <div className="flex-grow">
                                    <FormikField
                                        alignHorizontal={alignHorizontal}
                                        dataField={field}
                                        dirty={dirty}
                                        editable={false}
                                        errors={errors}
                                        fieldList={
                                            field?.dataList &&
                                            field?.dataList[locale] && {
                                                field: 'optionValue',
                                                options: field.dataList[
                                                    locale
                                                ]?.map(x => ({
                                                    value: x,
                                                    label: x,
                                                })),
                                            }
                                        }
                                        id={field.name}
                                        locale={locale}
                                        onChanges={() => {
                                            return null;
                                        }}
                                        touched={touched}
                                    />
                                    {/* <ErrorMessage
                                        className="text-red-600 text-sm -mt-2"
                                        component="div"
                                        name={field.name}
                                    /> */}
                                </div>
                                {editable && (
                                    <div className="flex space-x-4 mb-0.5">
                                        <button
                                            className="ml-4 text-admin border border-admin hover:bg-admin hover:text-white focus:ring-4 focus:outline-none focus:ring-admin_variant font-medium rounded-md p-1.5 text-center inline-flex items-center"
                                            onClick={() => {
                                                setEditingField(field);
                                            }}
                                            type="button"
                                        >
                                            <FaPen className="h-6 w-6" />
                                        </button>
                                        <DeleteConfirm
                                            locale={locale}
                                            onConfirm={() =>
                                                handleDeleteField(field.id)
                                            }
                                        >
                                            <button
                                                className="ml-4 text-red-600 border border-red-600 hover:bg-red-700 hover:text-white focus:ring-4 focus:outline-none focus:ring-admin_variant font-medium rounded-md p-1.5 text-center inline-flex items-center"
                                                type="button"
                                            >
                                                <FaTrash className="h-6 w-6" />
                                            </button>
                                        </DeleteConfirm>
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="flex flex-col justify-center py-3">
                            <hr className="w-full mx-auto border border-primary-accent" />
                            <button
                                className="button bg-primary text-white py-2 px-6 mt-6 disabled:bg-gray-300 disabled:cursor-not-allowed transition duration-150 ease-in-out"
                                disabled={!dirty || isSubmitting}
                                type="submit"
                            >
                                {isSubmitting
                                    ? translation.sending
                                    : translation.submit}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default SendingForm;
